import { InstagramOutlined, TikTokOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Space, Typography, Upload } from "antd";
import propTypes from "prop-types";
import React, { useState } from "react";
//redux
import { useDispatch, useSelector } from "react-redux";
import { useUploadMedia } from "../../../api/upload";
import {
  submitManualScreenshot,
  submitOnboardStep,
  uploadScreenshotSuccess,
} from "../../../redux/posts/postActions";
import {
  popLoading,
  pushLoading,
  result,
} from "../../../redux/shared/sharedActions";
//utils
import checkAuth from "../../../utils/checkAuth";
import { makeUniqueString, toTitleCase } from "../../../utils/stringUtils";

const { Text } = Typography;

const instagramRegex = new RegExp(/[\S]{11}/, "i");
const tiktokRegex = new RegExp(/^\d{18,20}$/, "i");

export default function SubmitManualPost(props) {
  const { post, text } = props;

  //state
  const [invalidShortcode, setInvalidShortcode] = useState(false);
  const [visible, setVisible] = useState(false);
  const [modalInput, setModalInput] = useState();
  const [defaultFileList, setDefaultFileList] = useState([]);

  //redux
  const dispatch = useDispatch();
  const roles = useSelector(state => state.auth.roles);
  const screenshot = useSelector(state => state.posts.screenshot);

  const uploadMedia = useUploadMedia();

  const manualSubmit = () => {
    if (
      screenshot &&
      defaultFileList.length &&
      screenshot.onboardId === post.onboardId
    )
      dispatch(submitManualScreenshot());
    else validateAndSubmitShortcode();
  };

  const validateAndSubmitShortcode = () => {
    const shortcodeId = modalInput && modalInput.trim();
    const shortcodeRegex =
      post.profilePlatform === "instagram" ? instagramRegex : tiktokRegex;

    const match = shortcodeRegex[Symbol.match](shortcodeId);
    //if valid shortcode regex, submit as json body
    if (match && match[0] === shortcodeId) {
      dispatch(
        submitOnboardStep(post.onboardId, {
          stepType: post.profilePlatform,
          shortcodeId,
        })
      );
      //reset
      setModalInput(null);
      setVisible(false);
    } else setInvalidShortcode(true);
  };

  const uploadImage = async options => {
    const { onSuccess, file } = options;

    const loadingId = "uploadManualScreenshot";
    dispatch(pushLoading(loadingId));

    try {
      // Ensure that the filename is unique (add random chars), otherwise Cloudflare will cache the image by name
      // and subsequent uploads will display the first uploaded image instead of the most recent one.
      // Likely not needed here, as an onboardId should be unique to each post, but keeping it for safety.
      const uniqueFileName = makeUniqueString(
        post.onboardId,
        "manual-screenshot"
      );

      const urls = await uploadMedia.mutateAsync({
        fileName: uniqueFileName,
        file,
        containerName: "manual-screenshot",
        mimeType: file.type,
      });

      onSuccess([urls.sourceMediaUrl]);
      dispatch(uploadScreenshotSuccess(post.onboardId, urls.sourceMediaUrl));
    } catch (err) {
      dispatch(
        result({
          status: `error`,
          title: `Upload manual screenshot failed`,
          subTitle: err.message,
        })
      );
    } finally {
      dispatch(popLoading(loadingId));
    }
  };

  const handleOnChange = ({ file, fileList, event }) => {
    //Using Hooks to update the state to the current filelist
    setDefaultFileList(fileList);
    //filelist - [{uid: "-1",url:'Some url to image'}]
  };

  if (!post || post.done || !checkAuth.has(roles, ["manager"])) return null;
  return (
    <span>
      <Button
        type="primary"
        key="close"
        icon={
          post.profilePlatform === "instagram" ? (
            <InstagramOutlined />
          ) : (
            <TikTokOutlined />
          )
        }
        onClick={() => setVisible(true)}
      >
        {text}
      </Button>
      <Modal
        title={`Submit ${toTitleCase(post.profilePlatform)} step for ${
          post.profileHandle
        }`}
        visible={visible}
        okText="Submit"
        onOk={() => manualSubmit()}
        // confirmLoading={confirmLoading}
        onCancel={() => {
          //close and reset input
          setModalInput(null);
          setVisible(false);
        }}
      >
        <Input
          placeholder={
            post.profilePlatform === "instagram"
              ? "B-vf01dnj7m"
              : "7579798975008525598"
          }
          value={modalInput}
          onChange={({ target: { value } }) => {
            setModalInput(value);
            setInvalidShortcode(false);
          }}
        />
        {/** TODO: Clarify for Tiktok */}
        {invalidShortcode && (
          <Text type="danger">
            {getShortcodeHelpText(post.profilePlatform)}
          </Text>
        )}
        {/* from https://stackblitz.com/edit/so-58128062-upload-progress */}
        <Space direction="vertical">
          <Upload
            accept="image/*"
            listType="picture"
            customRequest={uploadImage}
            onChange={handleOnChange}
            openFileDialogOnClick={!defaultFileList.length}
            defaultFileList={defaultFileList}
          >
            or <Typography.Link>upload a screenshot</Typography.Link>
          </Upload>
          {!post.interested && (
            <Text type="danger">
              You are submitting completion for an onboard marked as "
              <b>Not Interested</b>"
            </Text>
          )}
        </Space>
      </Modal>
    </span>
  );
}

function getShortcodeHelpText(platform) {
  if (platform === "instagram") {
    return "Please enter a valid Instagram shortcode. 11 characters, starts with a capital letter ";
  }
  if (platform === "tiktok") {
    return "Please enter a valid TikTok video ID (18-20 digit number) ";
  }
  return "Please enter a valid shortcode ";
}
SubmitManualPost.propTypes = {
  post: propTypes.object.isRequired,
  text: propTypes.node.isRequired,
};
